import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { getWatchlist, removeFromWatchlist, addToWatchlist } from "@/lib/watchlist";
import { Bookmark, Trash2, Plus, Star, TrendingUp } from "lucide-react";
import { toast } from "sonner";

// Preset options with proper yfinance symbols
const PRESETS = [
  { group: "NIFTY 50 Stocks", items: [
    { symbol: "RELIANCE.NS", label: "RELIANCE", type: "stock" },
    { symbol: "TCS.NS", label: "TCS", type: "stock" },
    { symbol: "HDFCBANK.NS", label: "HDFCBANK", type: "stock" },
    { symbol: "INFY.NS", label: "INFY", type: "stock" },
    { symbol: "ICICIBANK.NS", label: "ICICIBANK", type: "stock" },
    { symbol: "SBIN.NS", label: "SBIN", type: "stock" },
    { symbol: "BHARTIARTL.NS", label: "BHARTIARTL", type: "stock" },
    { symbol: "ITC.NS", label: "ITC", type: "stock" },
    { symbol: "LT.NS", label: "LT", type: "stock" },
    { symbol: "AXISBANK.NS", label: "AXISBANK", type: "stock" },
  ]},
  { group: "REITs", items: [
    { symbol: "EMBASSY.NS", label: "EMBASSY REIT", type: "reit" },
    { symbol: "MINDSPACE.NS", label: "MINDSPACE REIT", type: "reit" },
    { symbol: "BIRET.NS", label: "BROOKFIELD REIT", type: "reit" },
    { symbol: "NEXUS.NS", label: "NEXUS SELECT", type: "reit" },
  ]},
  { group: "Crypto", items: [
    { symbol: "BTC-USD", label: "BITCOIN", type: "crypto" },
    { symbol: "ETH-USD", label: "ETHEREUM", type: "crypto" },
    { symbol: "SOL-USD", label: "SOLANA", type: "crypto" },
    { symbol: "BNB-USD", label: "BNB", type: "crypto" },
    { symbol: "XRP-USD", label: "XRP", type: "crypto" },
    { symbol: "DOGE-USD", label: "DOGECOIN", type: "crypto" },
  ]},
  { group: "Indices", items: [
    { symbol: "^NSEI", label: "NIFTY 50", type: "index" },
    { symbol: "^BSESN", label: "SENSEX", type: "index" },
    { symbol: "^NSEBANK", label: "BANK NIFTY", type: "index" },
  ]},
];

export default function Watchlist() {
  const [list, setList] = useState(getWatchlist());
  const [customSym, setCustomSym] = useState("");

  useEffect(() => {
    const handler = () => setList(getWatchlist());
    window.addEventListener("ns_watchlist_changed", handler);
    return () => window.removeEventListener("ns_watchlist_changed", handler);
    // getWatchlist is a pure module fn, setList is stable — safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const symbols = list.map(x => x.symbol).join(",");
  const { data } = useQuery({
    queryKey: ["watchlist-quotes", symbols],
    queryFn: async () => symbols ? (await api.get(`/watchlist/quotes?symbols=${encodeURIComponent(symbols)}`)).data : { data: [] },
    refetchInterval: 45_000,
    enabled: symbols.length > 0,
  });

  const { data: mood } = useQuery({ queryKey: ["mood"], queryFn: async () => (await api.get("/mood-meter")).data, refetchInterval: 5*60_000 });
  const { data: filings } = useQuery({ queryKey: ["filings-watch"], queryFn: async () => (await api.get("/filings?limit=40")).data, refetchInterval: 60_000 });
  const { data: news } = useQuery({ queryKey: ["news-watch"], queryFn: async () => (await api.get("/news?limit=80")).data, refetchInterval: 60_000 });

  const quotes = data?.data || [];
  const quoteMap = Object.fromEntries(quotes.map(q => [q.symbol, q]));

  const add = (item) => {
    if (addToWatchlist(item)) toast.success(`Added ${item.label}`);
    else toast.info(`${item.label} already in watchlist`);
  };
  const addCustom = () => {
    const sym = customSym.trim().toUpperCase();
    if (!sym) return;
    if (addToWatchlist({ symbol: sym, label: sym.replace(/\.[A-Z]+$/, ""), type: "custom" })) {
      toast.success(`Added ${sym}`);
      setCustomSym("");
    } else toast.info("Already in watchlist");
  };
  const remove = (sym) => {
    removeFromWatchlist(sym);
    toast.success("Removed");
  };

  // Filter news/filings that mention any watchlist label
  const labels = list.map(x => x.label.toLowerCase()).filter(Boolean);
  const relevantFilings = (filings?.data || []).filter(f =>
    labels.some(l => (f.headline || "").toLowerCase().includes(l) || (f.company || "").toLowerCase().includes(l))
  ).slice(0, 10);
  const relevantNews = (news?.data || []).filter(n =>
    labels.some(l => (n.title || "").toLowerCase().includes(l))
  ).slice(0, 10);

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-8">
      <div className="flex items-center gap-3 mb-1">
        <Star className="w-5 h-5 text-amber-500" />
        <span className="text-xs font-mono-fin uppercase tracking-[0.3em] text-[#D91E2A] font-bold">Your Watchlist</span>
      </div>
      <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 mb-2">My Portfolio Tracker</h1>
      <p className="text-slate-600 mb-8 max-w-2xl text-sm sm:text-base">Pin any stock, REIT, index or crypto — get live prices plus the news & corporate filings that actually matter to your names.</p>

      {list.length === 0 && (
        <div className="rounded-xl bg-slate-50 border border-dashed border-slate-300 p-8 text-center mb-8" data-testid="watchlist-empty">
          <Bookmark className="w-10 h-10 mx-auto text-slate-400 mb-2" />
          <div className="font-heading font-semibold text-slate-700">Your watchlist is empty</div>
          <p className="text-xs text-slate-500 mt-1">Add tickers from the presets below or type any Yahoo Finance symbol.</p>
        </div>
      )}

      {list.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-10" data-testid="watchlist-grid">
          {list.map(item => {
            const q = quoteMap[item.symbol];
            const up = q ? q.change >= 0 : true;
            return (
              <div key={item.symbol} className="rounded-xl bg-white border border-slate-200 p-4 card-glow" data-testid={`watch-card-${item.symbol}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-slate-500">{item.type}</div>
                    <div className="font-heading font-bold text-slate-900">{item.label}</div>
                    <div className="text-[10px] font-mono-fin text-slate-400">{item.symbol}</div>
                  </div>
                  <button onClick={() => remove(item.symbol)} className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600" data-testid={`watch-remove-${item.symbol}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {q ? (
                  <>
                    <div className="font-mono-fin text-xl text-slate-900 font-bold">{q.price?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                    <div className={`font-mono-fin text-xs mt-0.5 font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`}>
                      {up ? "▲" : "▼"} {Math.abs(q.change || 0).toFixed(2)} ({Math.abs(q.change_pct || 0).toFixed(2)}%)
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-slate-400 font-mono-fin py-2">Fetching…</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add tickers */}
      <div className="rounded-xl bg-white border border-slate-200 p-5 mb-8 card-glow">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-heading font-bold text-slate-900">Add to Watchlist</h3>
          <div className="flex gap-2">
            <input value={customSym} onChange={e => setCustomSym(e.target.value)} onKeyDown={e => e.key === "Enter" && addCustom()} placeholder="Yahoo symbol (e.g. RELIANCE.NS)" className="px-3 py-2 rounded-md border border-slate-200 text-sm font-mono-fin" data-testid="watch-custom-input" />
            <button onClick={addCustom} className="px-3 py-2 rounded-md bg-[#D91E2A] text-white text-sm font-semibold flex items-center gap-1 hover:bg-[#B01820]" data-testid="watch-custom-add"><Plus className="w-3.5 h-3.5" /> Add</button>
          </div>
        </div>
        {PRESETS.map(g => (
          <div key={g.group} className="mb-4 last:mb-0">
            <div className="text-[10px] font-mono-fin uppercase tracking-widest text-slate-500 mb-2">{g.group}</div>
            <div className="flex flex-wrap gap-1.5">
              {g.items.map(it => (
                <button key={it.symbol} onClick={() => add(it)} className="text-xs px-2.5 py-1.5 rounded-md bg-slate-50 hover:bg-[#D91E2A]/10 hover:text-[#D91E2A] border border-slate-200 text-slate-700 font-semibold" data-testid={`watch-preset-${it.symbol}`}>
                  + {it.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {list.length > 0 && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="rounded-xl bg-white border border-slate-200 overflow-hidden card-glow">
              <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
                <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#D91E2A]" /> Filings for your names</h3>
              </div>
              <div className="divide-y divide-slate-200 max-h-[500px] overflow-y-auto">
                {relevantFilings.length === 0 && <div className="p-6 text-sm text-slate-500 font-mono-fin">No recent filings mention your watchlist. Filings update every 60s.</div>}
                {relevantFilings.map(f => (
                  <a key={f.id} href={f.link || "#"} target="_blank" rel="noopener noreferrer" className="block px-5 py-3 hover:bg-slate-50">
                    <div className="text-[10px] font-mono-fin uppercase text-[#D91E2A] font-semibold mb-1">{f.category} · {f.exchange}</div>
                    <div className="text-sm text-slate-900">{f.headline}</div>
                    {f.order_value && <span className="inline-block mt-1 text-[10px] font-mono-fin px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">{f.order_value}</span>}
                  </a>
                ))}
              </div>
            </div>
            <div className="rounded-xl bg-white border border-slate-200 overflow-hidden card-glow">
              <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
                <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#D91E2A]" /> News for your names</h3>
              </div>
              <div className="divide-y divide-slate-200 max-h-[500px] overflow-y-auto">
                {relevantNews.length === 0 && <div className="p-6 text-sm text-slate-500 font-mono-fin">No recent headlines mention your watchlist. Try adding more names.</div>}
                {relevantNews.map(n => (
                  <a key={n.id} href={n.link} target="_blank" rel="noopener noreferrer" className="block px-5 py-3 hover:bg-slate-50">
                    <div className="text-[10px] font-mono-fin uppercase text-[#D91E2A] font-semibold mb-1">{n.source}</div>
                    <div className="text-sm text-slate-900">{n.title}</div>
                  </a>
                ))}
              </div>
            </div>
          </div>
          {mood && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-5">
              <div className="text-[10px] font-mono-fin uppercase tracking-widest text-[#D91E2A] font-bold mb-1">Market Backdrop for your watchlist</div>
              <div className="flex items-baseline gap-3 mb-1">
                <span className="font-mono-fin text-3xl font-bold" style={{ color: mood.color }}>{Math.round(mood.score)}</span>
                <span className="font-semibold text-slate-700">{mood.zone}</span>
                <span className="text-xs text-slate-500 font-mono-fin">NIFTY 2W {mood.nifty_2w_return_pct >= 0 ? "+" : ""}{mood.nifty_2w_return_pct}% · VIX {mood.india_vix}</span>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{mood.ai_commentary}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
